// components/ForgotPassword.tsx
// Componente mejorado con glassmorphism e iconos Lucide

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  ArrowLeft,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Clock,
  Shield
} from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('Por favor ingresa tu email');
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setIsSuccess(true);
        setEmail('');
      } else {
        setMessage(data.message || 'Error al enviar el email');
        setIsSuccess(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('Error de conexión. Intenta nuevamente.');
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-6 mb-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              consultAR
            </h1>
            <p className="text-gray-600 text-sm">Tu plataforma de gestión profesional</p>
          </div>
          
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Recuperar Contraseña
            </h2>
            <p className="text-gray-600 text-sm">
              Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña
            </p>
          </div>
        </div>
        
        {/* Formulario */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-6">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Mensaje de estado */}
          {message && (
            <div className={`bg-white/40 backdrop-blur-md border rounded-2xl shadow-xl p-4 ${
              isSuccess 
                ? 'border-green-200' 
                : 'border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {isSuccess ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className={`text-sm font-semibold ${
                    isSuccess ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {isSuccess ? 'Email enviado' : 'Error'}
                  </h3>
                  <p className={`mt-1 text-sm ${
                    isSuccess ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Botón de envío */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Enviar enlace de recuperación
              </>
            )}
          </button>

          {/* Links de navegación */}
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-4">
            <div className="text-center space-y-3">
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al login
              </Link>
              <div className="text-gray-600 text-sm">
                ¿No tienes cuenta?{' '}
                <Link
                  to="/register"
                  className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                >
                  Regístrate aquí
                </Link>
              </div>
            </div>
          </div>
        </form>

        {/* Información adicional después de envío exitoso */}
        {isSuccess && (
          <div className="mt-6 bg-blue-100/50 backdrop-blur-md border border-blue-200 rounded-2xl shadow-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
              <h4 className="text-sm font-semibold text-blue-900">
                Próximos pasos:
              </h4>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-blue-800">
                <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Revisa tu bandeja de entrada</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-blue-800">
                <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Busca un email de "consultAR"</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-blue-800">
                <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Haz clic en el enlace (válido por 1 hora)</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-blue-800">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Si no lo ves, revisa spam/promociones</span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;